/**
 * The arrow toolbar: one floating pill above a selected arrow, in the same
 * style as the doc formatting bubble. Line shape, arrowheads, connection type
 * (its colour), label, animated flow and delete live here together.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, Trash2 } from 'lucide-react';
import { useArrowStore, type Arrow, type ArrowStyle } from '@/stores/arrowStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { kitFocus } from '@/components/partnr/kit';
import { cn } from '@/lib/utils';
import { arrowPaint, computeArrowGeometry, CONNECTION_TYPES } from './arrowGeometry';

const GAP = 12; // between the arrow (or its label) and the toolbar
const EDGE = 8; // keep this far from the window edges
const PILL_HEIGHT = 38;

/* ---------- glyphs: drawn to match lucide (24 box, 2px round strokes) ---------- */

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
      {children}
    </svg>
  );
}

const SHAPES: { value: ArrowStyle; label: string; glyph: React.ReactNode }[] = [
  { value: 'bezier', label: 'Curved', glyph: <path d="M4 19C4 10 20 14 20 5" /> },
  { value: 'straight', label: 'Straight', glyph: <path d="M4 20 20 4" /> },
  { value: 'step', label: 'Right angle', glyph: <path d="M4 19h8V5h8" /> },
];

const START_HEAD = (
  <>
    <path d="M5 12h15" />
    <path d="m10 7-5 5 5 5" />
  </>
);
const END_HEAD = (
  <>
    <path d="M4 12h15" />
    <path d="m14 7 5 5-5 5" />
  </>
);
const FLOW = <path d="M3 12h3M10.5 12h3M18 12h3" />;

/* ---------- controls ---------- */

function ToolButton({
  label,
  active,
  onClick,
  children,
  className,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={onClick}
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
            'transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100',
            active ? 'bg-signal-wash text-signal' : 'text-ink-2 hover:bg-wash hover:text-ink',
            kitFocus,
            className,
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function Divider() {
  return <span className="mx-0.5 h-4 w-px shrink-0 bg-line" aria-hidden />;
}

function Swatch({ color, className }: { color: string; className?: string }) {
  return (
    <span
      className={cn('h-3 w-3 shrink-0 rounded-full ring-1 ring-inset ring-black/10 dark:ring-white/15', className)}
      style={{ backgroundColor: arrowPaint(color) }}
      aria-hidden
    />
  );
}

function TypeMenu({ arrow, side }: { arrow: Arrow; side: 'top' | 'bottom' }) {
  const updateArrow = useArrowStore((s) => s.updateArrow);
  const [open, setOpen] = useState(false);
  const current = CONNECTION_TYPES.find((t) => t.value === (arrow.connection_type || 'default')) ?? CONNECTION_TYPES[0];

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Connection type: ${current.label}`}
          className={cn(
            'flex h-7 shrink-0 items-center gap-1.5 rounded-full pl-2 pr-1.5 text-[12px] font-medium text-ink-2',
            'transition-colors duration-150 ease-out hover:bg-wash hover:text-ink',
            open && 'bg-wash text-ink',
            kitFocus,
          )}
        >
          <Swatch color={arrow.stroke_color} />
          {current.label}
          <ChevronDown className={cn('h-3 w-3 text-ink-3 transition-transform duration-150 motion-reduce:transition-none', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="center"
        sideOffset={8}
        className="w-40 rounded-[14px] border-line bg-paper-raised p-1 shadow-float"
        onMouseDown={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div role="listbox" aria-label="Connection type">
          {CONNECTION_TYPES.map((t) => {
            const selected = t.value === current.value;
            return (
              <button
                key={t.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  updateArrow(arrow.id, { connection_type: t.value, stroke_color: t.color });
                  setOpen(false);
                }}
                className={cn(
                  'flex h-8 w-full items-center gap-2 rounded-[10px] px-2 text-left text-[13px] text-ink-2',
                  'transition-colors duration-150 ease-out hover:bg-wash hover:text-ink',
                  selected && 'text-ink',
                  kitFocus,
                )}
              >
                <Swatch color={t.color} />
                <span className="flex-1">{t.label}</span>
                {selected && <Check className="h-3.5 w-3.5 text-ink-3" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LabelControl({ arrow }: { arrow: Arrow }) {
  const updateArrow = useArrowStore((s) => s.updateArrow);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(arrow.label || '');

  useEffect(() => {
    if (!editing) setValue(arrow.label || '');
  }, [arrow.label, editing]);

  // A new arrow selected while typing: close the field without saving into it
  useEffect(() => {
    setEditing(false);
  }, [arrow.id]);

  const commit = useCallback(() => {
    const next = value.trim() || null;
    if (next !== (arrow.label || null)) updateArrow(arrow.id, { label: next });
    setEditing(false);
  }, [arrow.id, arrow.label, updateArrow, value]);

  if (editing) {
    return (
      <form
        className="flex items-center"
        onSubmit={(e) => {
          e.preventDefault();
          commit();
        }}
      >
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            // Keep Backspace and Escape away from the canvas shortcuts
            e.stopPropagation();
            if (e.key === 'Escape') {
              e.preventDefault();
              setValue(arrow.label || '');
              setEditing(false);
            }
          }}
          maxLength={60}
          placeholder="Name this link"
          aria-label="Arrow label"
          className="h-7 w-40 rounded-full bg-paper-sunken px-3 text-[13px] text-ink outline-none placeholder:text-ink-3 focus-visible:ring-2 focus-visible:ring-signal"
        />
      </form>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'h-7 max-w-[9rem] shrink-0 truncate rounded-full px-2.5 text-[12px] font-medium',
        'transition-colors duration-150 ease-out hover:bg-wash hover:text-ink',
        arrow.label ? 'text-ink' : 'text-ink-3',
        kitFocus,
      )}
      title={arrow.label ? 'Edit label' : undefined}
    >
      {arrow.label || 'Add label'}
    </button>
  );
}

/* ---------- toolbar ---------- */

interface ArrowPropertiesPanelProps {
  /** The canvas container's box on screen; the canvas transform is relative to it */
  containerRect?: DOMRect | null;
}

export function ArrowPropertiesPanel({ containerRect }: ArrowPropertiesPanelProps) {
  const { selectedArrowId, arrows, updateArrow, deleteArrow, setSelectedArrow } = useArrowStore();
  const { items, panX, panY, zoom } = useCanvasStore();
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(0);

  const arrow = selectedArrowId ? arrows.find((a) => a.id === selectedArrowId) ?? null : null;

  // Track the toolbar's width so it can be clamped to the window (it grows
  // while the label field is open). Width only: the observer moves the
  // toolbar, never resizes it, so it cannot loop.
  const arrowId = arrow?.id;
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    setPanelWidth(el.offsetWidth);
    let last = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      const w = el.offsetWidth;
      if (Math.abs(w - last) > 1) {
        last = w;
        setPanelWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [arrowId]);

  if (!arrow) return null;

  const startItem = arrow.start_item_id ? items.find((i) => i.id === arrow.start_item_id) : null;
  const endItem = arrow.end_item_id ? items.find((i) => i.id === arrow.end_item_id) : null;
  const geo = computeArrowGeometry(arrow, startItem, endItem);
  if (!geo) return null;

  // Canvas to screen: the canvas layer is `scale(zoom) translate(pan)`
  const originX = containerRect?.left ?? 0;
  const originY = containerRect?.top ?? 0;
  const toScreenY = (y: number) => (y + panY) * zoom + originY;
  const midX = (geo.midpoint.x + panX) * zoom + originX;
  // Clear the whole line, and the label pill (22 canvas px, centred on the line)
  const labelHalf = arrow.label ? 11 : 0;
  const lineTop = toScreenY(Math.min(geo.top, geo.midpoint.y - labelHalf)) - GAP;
  const lineBottom = toScreenY(Math.max(geo.bottom, geo.midpoint.y + labelHalf)) + GAP;

  // Above the arrow by default; below it when there is no room up there;
  // pinned to the window edge when the arrow fills the screen
  const above = lineTop - PILL_HEIGHT >= EDGE;
  const top = above
    ? lineTop - PILL_HEIGHT
    : Math.max(EDGE, Math.min(lineBottom, window.innerHeight - PILL_HEIGHT - EDGE));
  const half = panelWidth / 2;
  const left = Math.max(EDGE, Math.min(midX - half, window.innerWidth - panelWidth - EDGE));

  const shape = arrow.style || 'bezier';

  return (
    <motion.div
      ref={panelRef}
      key={arrow.id}
      role="toolbar"
      aria-label="Arrow"
      data-arrow-toolbar=""
      className="fixed z-50 flex select-none items-center gap-0.5 rounded-full border border-line bg-paper-raised p-1 shadow-float"
      style={{ left, top, visibility: panelWidth ? 'visible' : 'hidden' }}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: above ? 4 : -4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={reduceMotion ? { duration: 0.12 } : { type: 'spring', stiffness: 500, damping: 38 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {SHAPES.map((s) => (
        <ToolButton key={s.value} label={s.label} active={shape === s.value} onClick={() => updateArrow(arrow.id, { style: s.value })}>
          <Glyph>{s.glyph}</Glyph>
        </ToolButton>
      ))}

      <Divider />

      <ToolButton
        label={arrow.start_arrowhead ? 'Remove start arrowhead' : 'Add start arrowhead'}
        active={arrow.start_arrowhead}
        onClick={() => updateArrow(arrow.id, { start_arrowhead: !arrow.start_arrowhead })}
      >
        <Glyph>{START_HEAD}</Glyph>
      </ToolButton>
      <ToolButton
        label={arrow.end_arrowhead ? 'Remove end arrowhead' : 'Add end arrowhead'}
        active={arrow.end_arrowhead}
        onClick={() => updateArrow(arrow.id, { end_arrowhead: !arrow.end_arrowhead })}
      >
        <Glyph>{END_HEAD}</Glyph>
      </ToolButton>

      <Divider />

      <TypeMenu arrow={arrow} side={above ? 'top' : 'bottom'} />

      <Divider />

      <LabelControl arrow={arrow} />

      <Divider />

      <ToolButton
        label={arrow.animated ? 'Stop the flow' : 'Animate the flow'}
        active={arrow.animated ?? false}
        onClick={() => updateArrow(arrow.id, { animated: !arrow.animated })}
      >
        <Glyph>{FLOW}</Glyph>
      </ToolButton>
      <ToolButton
        label="Delete arrow"
        onClick={() => {
          deleteArrow(arrow.id);
          setSelectedArrow(null);
        }}
        className="hover:bg-danger/10 hover:text-danger"
      >
        <Trash2 className="h-4 w-4" />
      </ToolButton>
    </motion.div>
  );
}
